import { useCallback, useMemo, useRef } from 'react';
import { AutoConfirmDefaultOn } from '~/constants/ff.posthog';
import { useFavoriteProfessionals } from '~/domain/FavoriteProfessionals';
import { fireShiftRequestAutoconfirmExperimentExposed } from '~/posthogs/events';
import { useFeatureFlagVariant } from './useFeatureFlagVariant';
import { usePaymentEligibility } from './usePaymentEligibility';
import { usePosthog } from './usePosthog';

export interface IAutoConfirmDefaults {
  favorites: boolean;
  eligibleProfessionals: boolean;
}

export interface IUseAutoConfirmExperimentResult {
  /** Office is bucketed into the experiment's `test` variant. */
  isTestVariant: boolean;
  /** Office can pay — the qualifier for both exposure and default-on. */
  isEligible: boolean;
  /**
   * Payment eligibility is still resolving. Consumers should default to the
   * unlocked/valid state while this is true so the payment-locked banner never
   * flashes before the payment query settles.
   */
  eligibilityLoading: boolean;
  /** Office has at least one favorite professional. */
  hasFavorites: boolean;
  /** Favorites are still loading; defer any favorites-dependent decision. */
  favoritesLoading: boolean;
  /** Favorites toggle should render inactive (resolved: no favorites). */
  favoritesDisabled: boolean;
  /** Auto-confirm should be defaulted ON for this office (test + eligible). */
  shouldDefaultOn: boolean;
  /** Defaults to apply when `shouldDefaultOn` — favorites only when the office has any. */
  defaultAutoConfirm: IAutoConfirmDefaults;
  /** Fires the experiment exposure event once, and only when the office is eligible. */
  exposeExperiment: () => void;
}

/**
 * Drives the Auto-Confirm default-on experiment on the shift request form.
 *
 * The experiment defaults auto-confirm ON for the `test` variant, but only for offices that
 * can pay (`usePaymentEligibility`). Eligibility also gates exposure: `exposeExperiment`
 * fires the custom exposure event only for eligible offices, so ineligible offices never
 * enter the control group and skew the results.
 *
 * `favoritesDisabled` (favorites toggle inactive when the office has none) is a general
 * update that applies to both variants; it is surfaced here because it shares the favorites
 * lookup the experiment already needs.
 */
export const useAutoConfirmExperiment = (
  officeId?: string,
): IUseAutoConfirmExperimentResult => {
  const posthog = usePosthog();
  const isTestVariant = useFeatureFlagVariant(AutoConfirmDefaultOn, 'test');

  // `eligibilityLoading` lets consumers default to the unlocked/valid state while the
  // payment query is in flight (isEligible is `false` until it resolves), so the
  // payment-locked banner never flashes before data arrives.
  const { isEligible, isLoading: eligibilityLoading } = usePaymentEligibility(officeId);

  const { data: favorites, isLoading: favoritesLoading } = useFavoriteProfessionals({
    officeId: officeId ?? '',
    enabled: Boolean(officeId),
  });
  const hasFavorites = (favorites ?? []).length > 0;

  const favoritesDisabled = !favoritesLoading && !hasFavorites;
  const shouldDefaultOn = isTestVariant && isEligible;

  // Stable identity so consumers can safely use it as an effect dependency.
  const defaultAutoConfirm = useMemo<IAutoConfirmDefaults>(
    () => ({ eligibleProfessionals: true, favorites: hasFavorites }),
    [hasFavorites],
  );

  const exposedRef = useRef(false);
  const exposeExperiment = useCallback(() => {
    if (exposedRef.current || !isEligible) return;
    exposedRef.current = true;

    const variant = posthog?.getFeatureFlagVariant(AutoConfirmDefaultOn);
    fireShiftRequestAutoconfirmExperimentExposed({
      flagKey: AutoConfirmDefaultOn,
      variant: typeof variant === 'string' ? variant : String(variant),
    });
  }, [isEligible, posthog]);

  return {
    isTestVariant,
    isEligible,
    eligibilityLoading,
    hasFavorites,
    favoritesLoading,
    favoritesDisabled,
    shouldDefaultOn,
    defaultAutoConfirm,
    exposeExperiment,
  };
};
